import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import { CommonService } from '../../services/common.service';
import { ErrorService } from '../../services/error/error.service';
import { FacilitiesFormService } from './facilities-form.service';
import { EnvironmentService } from '../../services/environment.service';
import { AllDropdownKeys, UrlSegments } from '../jurisdiction.constants';
import { FormTypesData, KeyValueData } from '../../domains/common.domain';
import { LocalStorageService } from '../../services/local-storage.service';
import { DestroyableComponent } from '../../common/destroyable.component';
import { CommunicationService } from '../../services/communication.service';
import { ComponentFormData, LookupOptionsData } from '../jurisdiction.domain';
import { FacilityFormType, InstallationChangeType, InstallationFlowType } from '../../constants/app.constants';
import { OrderHistoryResponse } from '../../+shared/components/order-history/order-history.domain';
import { NewFacilityComponent } from './base-data/new-facility/new-facility.component';
import { MeterDataComponent } from './meter-data/meter-data.component';
import { BaseDataComponent } from './base-data/base-data.component';
import { BaseDataObject, FacilityDataObject, FiringSystemDataObject, FurnaceDataObject, MeterDataObject } from './facilities-form.domain';

@Component({
  selector: 'app-facilities-form',
  styleUrls: ['./facilities-form.component.scss'],
  templateUrl: 'facilities-form.component.html',
})
export class FacilitiesFormComponent extends DestroyableComponent implements OnInit {
  @Input() showData: boolean;
  @Input() formIndex: number = null;
  @Input() isReadOnly: boolean;
  @Input() facilitiesData: any;
  @Input() allowDelete: boolean;
  @Input() isMeterInstallation: boolean;
  @Input() currentFormType: FormTypesData;

  @Output() onCloneTechnicalData: EventEmitter<any>;
  @Output() onDeleteTechnicalData: EventEmitter<any>;

  @ViewChild(BaseDataComponent) baseDataEl!: BaseDataComponent;
  @ViewChild(MeterDataComponent) meterDataEl!: MeterDataComponent;
  @ViewChild(NewFacilityComponent) newFacilityEl!: NewFacilityComponent;

  facilitiesDataFG: {
    baseData: BaseDataObject[];
    meterData: MeterDataObject[];
    newFacilityData: FacilityDataObject;
    oldMeterData: MeterDataObject;
    construction: any;
    exhaustSystem: any;
    fireplaceVentilation: any;
    releases: any;
    meterInstallation: any;
    furnaceData: FurnaceDataObject;
    firingSystemData: FiringSystemDataObject;
    chimneySweepData: any;
  };
  baseDataCount: number[];
  currentFlowType: string;
  externalApplicationId: string;
  facilityTypes: FormTypesData[];
  installationFlowType: KeyValueData;
  showFormElement: boolean;
  showNewMeterSection: boolean;

  gasInstallationChangeTypesKey: string;
  gasInstallationChangeTypes: LookupOptionsData[];
  heatingInstallationChangeTypesKey: string;
  heatingInstallationChangeTypes: LookupOptionsData[];
  infoBubblesData: {
    [key: string]: boolean;
  };
  installationChangeTypesKey: string;
  installationChangeTypes: LookupOptionsData[];
  waterInstallationChangeTypesKey: string;
  waterInstallationChangeTypes: LookupOptionsData[];
  installationChangeType: string;
  powerInstallationChangeType: string;
  gasInstallationChangeType: string;
  waterInstallationChangeType: string;
  heatingInstallationChangeType: string;

  changePower: string;
  changeGas: string;
  changeWater: string;
  changeHeat: string;

  systemId: string;
  selectedMeasuringPointOperator: string;

  isLoading: boolean = true;

  constructor(
    private errorService: ErrorService,
    private commonService: CommonService,
    private localStorageService: LocalStorageService,
    private communicationService: CommunicationService,
    private facilitiesFormService: FacilitiesFormService,
    public environmentService: EnvironmentService
  ) {
    super();
    this.onCloneTechnicalData = new EventEmitter<any>();
    this.onDeleteTechnicalData = new EventEmitter<any>();
  }

  ngOnInit(): void {
    this.initProperties();

    if (this.isReadOnly) {
      this.initInstallationChangeTypes();
      this.getAppHistoryLog();
    } else {
      this.parseTechnicalData();
    }

    this.handleFormDataChanges();
    this.setLoading(false);
  }

  removeBaseData(index: number): void {
    const facilityId = this.facilitiesDataFG.baseData[index]?.facilityId;

    let requestUrl: string;
    switch (this.currentFormType.index) {
      case 0:
        requestUrl = UrlSegments.UpdatePowerFacility;
        break;
      case 1:
        requestUrl = UrlSegments.UpdateGasFacility;
        break;
      case 2:
        requestUrl = UrlSegments.UpdateWaterFacility;
        break;
    }
    if (facilityId && requestUrl) {
      this.communicationService.deleteData(`${requestUrl}=${facilityId}`).subscribe(
        response => {
          this.errorService.throwErrorIfFailure(response, true);
          this.handleDeviceData(index);
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.handleDeviceData(index);
    }
  }

  addBaseData(): void {
    this.facilitiesData.baseData.push({});
    this.facilitiesDataFG.baseData = this.facilitiesData.baseData;
    this.baseDataCount.push(this.baseDataCount.length ? this.baseDataCount[this.baseDataCount.length - 1] + 1 : 0);
  }

  cloneTechnicalData(copyExisting: boolean = false) {
    this.onCloneTechnicalData.emit(copyExisting ? this.deepCopy(this.facilitiesData) : this.getEmptyTechnicalData());
  }

  deleteTechnicalData() {
    this.onDeleteTechnicalData.emit(this.formIndex);
  }

  isValidForm(): boolean {
    return this.baseDataEl?.isValid() && this.isMeterInstallation && (this.meterDataEl?.isValid() ?? true) && this.newFacilityEl?.isValid();
  }

  getTechDataFormIndex() {
    return this.formIndex != null && this.environmentService.copyExistingTechnicalDataConfiguration ? `FORM ${this.formIndex + 1}` : '';
  }

  private deepCopy(obj) {
    return obj ? JSON?.parse(JSON.stringify(obj)) : {};
  }

  private setLoading(state) {
    this.isLoading = state;
  }

  private initProperties() {
    this.showFormElement = false;
    this.installationFlowType = InstallationFlowType;
    this.externalApplicationId = this.commonService.installationApplicationId;
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
    this.facilityTypes = this.localStorageService.getItem('selected_facilities');
    this.baseDataCount = [];

    this.infoBubblesData = { ...this.environmentService.getInfoBubblesData('facilitiesData') } || {};
    this.infoBubblesData.showBaseDataHeaderInfo =
      this.infoBubblesData.showBaseDataHeaderInfo ||
      (this.environmentService.hideBaseDataInfoForWater && this.currentFormType.key !== 'water');

    this.setInstallationChangeType(this.currentFormType.key);

    this.showNewMeterSection =
      (!(this.installationChangeType === InstallationChangeType.SystemSeparation) && this.environmentService.showNewMeterSection) ||
      (!this.environmentService.showNewMeterSection && this.currentFlowType !== InstallationFlowType.UpdateInstallation);

    this.changePower = '0';
    this.changeGas = '0';
    this.changeWater = '0';
    this.changeHeat = '0';
    this.facilityTypes.forEach(facility => {
      if (facility.key === FacilityFormType[0].key) {
        this.changePower = '1';
      } else if (facility.key === FacilityFormType[1].key) {
        this.changeGas = '1';
      } else if (facility.key === FacilityFormType[2].key) {
        this.changeWater = '1';
      } else if (facility.key === FacilityFormType[3].key) {
        this.changeHeat = '1';
      }
    });
  }

  private initInstallationChangeTypes() {
    this.installationChangeTypesKey = AllDropdownKeys.InstallationChangeType;
    this.gasInstallationChangeTypesKey = AllDropdownKeys.InstallationChangeTypeGas;
    this.heatingInstallationChangeTypesKey = AllDropdownKeys.InstallationChangeTypeHeating;
    this.waterInstallationChangeTypesKey = AllDropdownKeys.InstallationChangeTypeWater;
    this.installationChangeTypes = this.commonService.getDropdownData(this.installationChangeTypesKey);
    this.gasInstallationChangeTypes = this.commonService.getDropdownData(this.gasInstallationChangeTypesKey);
    this.heatingInstallationChangeTypes = this.commonService.getDropdownData(this.heatingInstallationChangeTypesKey);
    this.waterInstallationChangeTypes = this.commonService.getDropdownData(this.waterInstallationChangeTypesKey);
  }

  private handleDeviceData(currentIndex: number) {
    this.baseDataCount.splice(currentIndex, 1);
    this.facilitiesData.baseData.splice(currentIndex, 1);
    this.facilitiesDataFG.baseData = this.facilitiesData.baseData;
  }

  private getAppHistoryLog() {
    const applicationId = this.commonService.installationApplicationData?.application_id;
    this.commonService.getAppHistoryLog(applicationId, 'application_submitted').subscribe(
      (response: OrderHistoryResponse) => {
        this.errorService.throwErrorIfFailure(response, true);
        this.commonService.applicationSubmissionDate = response.apphistory[0]?.log_date || '';

        this.facilitiesDataFG = this.facilitiesData;
        this.setInstallationChangeTypes();
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private handleFormDataChanges(): void {
    this.facilitiesFormService.registerForComponentFormUpdate.subscribe((data: ComponentFormData) => {
      if (data.component === 'newFacility') {
        this.facilitiesData.newFacilityData = data.formData.value;
        this.selectedMeasuringPointOperator = data.formData.value.measuringPointOperator;
      } else if (data.component === 'baseData') {
        this.facilitiesData.baseData[data.index] = data.formData.value;
      } else if (data.component === 'fireplaceVentilation') {
        const fireplaceVentilationValues = data.formData.value;
        this.facilitiesData.firingSystemData.airEmissionSystemCheckbox = fireplaceVentilationValues.airEmissionSystemCheckbox ?? false;
        this.facilitiesData.firingSystemData.airSupplyOpenAirCheckbox = fireplaceVentilationValues.airSupplyOpenAirCheckbox ?? false;
        this.facilitiesData.firingSystemData.emissionGasCheckbox = fireplaceVentilationValues.emissionGasCheckbox ?? false;
        this.facilitiesData.firingSystemData.withBlowerCheckbox = fireplaceVentilationValues.withBlowerCheckbox ?? false;
        this.facilitiesData.firingSystemData.withFlowProtectionCheckbox = fireplaceVentilationValues.withFlowProtectionCheckbox ?? false;
        this.facilitiesData.firingSystemData.withoutBlowerCheckbox = fireplaceVentilationValues.withoutBlowerCheckbox ?? false;
        this.facilitiesData.firingSystemData.withoutFlowProtectionCheckbox =
          fireplaceVentilationValues.withoutFlowProtectionCheckbox ?? false;
        this.facilitiesData[data.component] = data.formData.value;
      } else {
        this.facilitiesData[data.component] = data.formData.value;
      }
    });
  }

  private setInstallationChangeType(facilityType: string) {
    const locationData = this.localStorageService.getItem('location_data') || null;

    if (this.currentFlowType === InstallationFlowType.UpdateInstallation) {
      if (facilityType === FacilityFormType[0].key) {
        if (this.commonService.installationApplicationData?.powerinstallation.hasOwnProperty('sk_inst_change_type')) {
          this.installationChangeType = this.commonService.installationApplicationData.powerinstallation.sk_inst_change_type;
        } else if (locationData && locationData.powerType) {
          this.installationChangeType = locationData.powerType;
        }
      } else if (facilityType === FacilityFormType[1].key) {
        if (this.commonService.installationApplicationData?.gasinstallation.hasOwnProperty('sk_inst_change_type_gas')) {
          this.installationChangeType = this.commonService.installationApplicationData.gasinstallation.sk_inst_change_type_gas;
        } else if (locationData && locationData.gasType) {
          this.installationChangeType = locationData.gasType;
        }
      } else if (facilityType === FacilityFormType[2].key) {
        if (this.commonService.installationApplicationData?.waterinstallation.hasOwnProperty('sk_inst_change_type_water')) {
          this.installationChangeType = this.commonService.installationApplicationData.waterinstallation.sk_inst_change_type_water;
        } else if (locationData && locationData.waterType) {
          this.installationChangeType = locationData.waterType;
        }
      } else if (facilityType === FacilityFormType[3].key) {
        if (this.commonService.installationApplicationData?.districtheatinstallation.hasOwnProperty('sk_inst_change_type_districtheat')) {
          this.installationChangeType =
            this.commonService.installationApplicationData.districtheatinstallation.sk_inst_change_type_districtheat;
        } else if (locationData && locationData.heatingType) {
          this.installationChangeType = locationData.heatingType;
        }
      }
    }
  }

  private parseTechnicalData(): void {
    this.initFacilitiesDataFormGroup();
    this.applyFacilitiesDataToFormGroup();
    this.setInstallationChangeTypes();
  }

  private initFacilitiesDataFormGroup() {
    this.facilitiesDataFG = this.getEmptyTechnicalData();
  }

  private getEmptyTechnicalData() {
    return {
      baseData: [] as BaseDataObject[],
      meterData: [] as MeterDataObject[],
      newFacilityData: {} as FacilityDataObject,
      oldMeterData: {} as MeterDataObject,
      construction: {},
      exhaustSystem: {},
      fireplaceVentilation: {},
      releases: {},
      meterInstallation: {},
      furnaceData: {} as FurnaceDataObject,
      firingSystemData: {} as FiringSystemDataObject,
      chimneySweepData: {},
    };
  }

  private applyFacilitiesDataToFormGroup() {
    this.facilitiesDataFG.baseData = [...this.facilitiesData.baseData];
    this.facilitiesDataFG.meterData = [...this.facilitiesData.meterData];
    this.facilitiesDataFG.newFacilityData = this.deepCopy(this.facilitiesData.newFacilityData);
    this.facilitiesDataFG.oldMeterData = this.deepCopy(this.facilitiesData.oldMeterData);
    this.facilitiesDataFG.construction = this.deepCopy(this.facilitiesData.construction);
    this.facilitiesDataFG.exhaustSystem = this.deepCopy(this.facilitiesData.exhaustSystem);
    this.facilitiesDataFG.fireplaceVentilation = this.deepCopy(this.facilitiesData.fireplaceVentilation);
    this.facilitiesDataFG.releases = this.deepCopy(this.facilitiesData.releases);
    this.facilitiesDataFG.meterInstallation = this.deepCopy(this.facilitiesData.meterInstallation);
    this.facilitiesDataFG.furnaceData = this.deepCopy(this.facilitiesData.furnaceData);
    this.facilitiesDataFG.firingSystemData = this.deepCopy(this.facilitiesData.firingSystemData);
    this.facilitiesDataFG.chimneySweepData = this.deepCopy(this.facilitiesData.chimneySweepData);
  }

  private setInstallationChangeTypes() {
    this.showFormElement = true;
    if (this.facilitiesDataFG.baseData.length) {
      this.baseDataCount = [...Array(this.facilitiesDataFG.baseData.length).keys()];
    } else {
      this.addBaseData();
    }
    const installationApplicationData = this.commonService.installationApplicationData;
    if (installationApplicationData && this.changePower === '1') {
      this.powerInstallationChangeType = installationApplicationData.powerinstallation.sk_inst_change_type || '';
    }
    if (installationApplicationData && this.changeGas === '1') {
      this.gasInstallationChangeType = installationApplicationData.gasinstallation.sk_inst_change_type_gas || '';
    }
    if (installationApplicationData && this.changeWater === '1') {
      this.waterInstallationChangeType = installationApplicationData.waterinstallation.sk_inst_change_type_water || '';
    }
    if (installationApplicationData && this.changeHeat === '1') {
      this.heatingInstallationChangeType = installationApplicationData.districtheatinstallation.sk_inst_change_type_districtheat || '';
    }
  }
}
